<script setup lang="ts">
  import type { DropdownMenuItem } from '#ui/types'

  const { loggedIn, user, clear } = useUserSession()
  const colorMode = useColorMode()

  watch(loggedIn, () => {
    if (!loggedIn.value) {
      navigateTo('/')
    }
  })

  const isDarkMode = computed({
    get: () => colorMode.preference === 'dark',
    set: () =>
      (colorMode.preference = colorMode.value === 'dark' ? 'light' : 'dark'),
  })

  useHead({
    htmlAttrs: { lang: 'en' },
    link: [{ rel: 'icon', href: '/icon.png' }],
  })

  useSeoMeta({
    viewport: 'width=device-width, initial-scale=1, maximum-scale=1',
    title: 'Atidone',
    description:
      'A Nuxt demo hosted with edge-side rendering, authentication and queyring a Cloudflare D1 database',
    ogImage: '/social-image.png',
    twitterImage: '/social-image.png',
    twitterCard: 'summary_large_image',
  })

  const items = [
    [
      {
        label: 'Profile',
        icon: 'i-lucide-user',
        to: '/profile',
      },
    ],
    [
      {
        label: 'Logout',
        icon: 'i-lucide-log-out',
        onSelect: clear,
      },
    ],
  ] satisfies DropdownMenuItem[][]
</script>

<template>
  <UApp>
    <UContainer class="min-h-screen flex flex-col my-4">
      <div class="mb-2 text-right">
        <UButton
          square
          variant="ghost"
          color="neutral"
          :icon="
            $colorMode.preference === 'dark' ||
            $colorMode.preference === 'system'
              ? 'i-lucide-moon'
              : 'i-lucide-sun'
          "
          @click="isDarkMode = !isDarkMode"
        />
      </div>

      <UCard variant="subtle">
        <template #header>
          <h3 class="text-lg font-semibold leading-6">
            <NuxtLink to="/"> Atidone </NuxtLink>
          </h3>
          <UButton
            v-if="!loggedIn"
            to="/login"
            icon="i-lucide-log-in"
            label="Login"
            color="primary"
            size="xs"
          />
          <UButton
            v-if="!loggedIn"
            to="/api/auth/github"
            icon="i-simple-icons-github"
            label="GitHub"
            color="neutral"
            size="xs"
            external
          />
          <div v-else class="flex flex-wrap -mx-2 sm:mx-0">
            <UButton
              to="/todos"
              icon="i-lucide-list"
              label="Todos"
              :color="$route.path === '/todos' ? 'primary' : 'neutral'"
              variant="ghost"
            />
            <UButton
              to="/optimistic-todos"
              icon="i-lucide-sparkles"
              label="Optimistic Todos"
              :color="
                $route.path === '/optimistic-todos' ? 'primary' : 'neutral'
              "
              variant="ghost"
            />
            <UDropdownMenu v-if="user" :items="items">
              <UButton
                color="neutral"
                variant="ghost"
                trailing-icon="i-lucide-chevron-down"
              >
                <UAvatar
                  v-if="user.login && !user.email"
                  :src="`https://github.com/${user.login}.png`"
                  :alt="user.login"
                  size="3xs"
                />
                <UAvatar
                  v-else
                  :alt="user.name || user.email || user.login"
                  size="3xs"
                />
                {{ user.name || user.login }}
              </UButton>
            </UDropdownMenu>
          </div>
        </template>
        <NuxtPage />
      </UCard>

      <footer class="text-center mt-2">
        <NuxtLink
          href="https://github.com/atinux/atidone"
          target="_blank"
          class="text-sm text-neutral-500 hover:text-neutral-700"
        >
          GitHub
        </NuxtLink>
        ·
        <NuxtLink
          href="https://twitter.com/atinux"
          target="_blank"
          class="text-sm text-neutral-500 hover:text-neutral-700"
        >
          Twitter
        </NuxtLink>
      </footer>
    </UContainer>
  </UApp>
</template>
